import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import api from "@/lib/api";
import { DUOCLAIR_WHATSAPP } from "@/config";
import { DuoClairLogo } from "@/components/DuoClairLogo";
import { SiteMenu } from "@/components/SiteMenu";
import { MaskedLines } from "@/components/MaskedLines";
import { VideoPlayer } from "@/components/VideoPlayer";
import { LeadForm } from "@/components/LeadForm";

// Collegamenti diretti condivisibili: hash pubblici -> sezioni della landing o schede di /servizi
const SERVICE_HASH = {
  "social-identita": "social-identita",
  "video-contenuti-ai": "video-contenuti-ai",
  "video-ai": "video-contenuti-ai",
  "siti-web-automazioni": "siti-web-automazioni",
  "siti-automazioni": "siti-web-automazioni",
  "recensioni-qr-nfc": "recensioni-qr-nfc",
  "gestione-completa": "gestione-completa",
  "gestione-digitale": "gestione-completa",
};
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function LandingPage() {
  const lenisRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => setVideoUrl(r.data.video_url)).catch(() => {});
  }, []);

  // Return from /servizi: reopen the existing form or restore the previous scroll position
  useEffect(() => {
    if (location.state?.openForm) {
      setFormOpen(true);
      window.history.replaceState({}, "");
    } else if (location.state?.scrollTarget) {
      const target = location.state.scrollTarget;
      window.history.replaceState({}, "");
      setTimeout(() => scrollTo(target), 250);
    } else if (location.state?.restoreScroll) {
      const y = Number(sessionStorage.getItem("dc_scroll") || 0);
      if (y > 0) {
        [150, 600, 1400].forEach((delay) => {
          setTimeout(() => {
            if (lenisRef.current) lenisRef.current.scrollTo(y, { immediate: true, force: true });
            else window.scrollTo(0, y);
          }, delay);
        });
      }
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Collegamenti diretti (#video, #soluzioni, #contatti, #analisi, servizi -> /servizi#slug)
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (!hash) return;
    const serviceTarget = SERVICE_HASH[hash];
    if (serviceTarget) {
      navigate(`/servizi#${serviceTarget}`, { replace: true });
      return;
    }
    if (hash === "analisi") {
      setFormOpen(true);
      return;
    }
    if (hash === "video" || hash === "soluzioni" || hash === "contatti") {
      let tries = 0;
      let cancelled = false;
      const attempt = () => {
        if (cancelled) return;
        if (document.getElementById(hash)) scrollTo(`#${hash}`);
        else if (tries++ < 25) setTimeout(attempt, 120);
      };
      const t = setTimeout(attempt, 200);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const scrollTo = (target) => {
    if (lenisRef.current) lenisRef.current.scrollTo(target, { offset: -64 });
    else document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  };

  const goToServizi = () => {
    sessionStorage.setItem("dc_scroll", String(Math.round(window.scrollY)));
    navigate("/servizi");
  };

  return (
    <div className="min-h-screen text-foreground overflow-x-clip" data-testid="landing-page">
      {/* Header — minimal, left aligned */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/40 backdrop-blur-md border-b border-white/5" data-testid="site-header">
        <div className="max-w-3xl mx-auto px-5 h-14 sm:h-16 flex items-center justify-between">
          <DuoClairLogo />
          <SiteMenu />
        </div>
      </header>

      <div className="relative z-10">
        {/* Hero — single integrated brand block, real text below */}
        <section className="relative pt-[76px] sm:pt-20 pb-10">
          <div className="relative text-center px-5 max-w-xl mx-auto w-full">
            <div
              className="hero-brand-enter mx-auto flex w-full max-w-[520px] select-none flex-col items-center"
              data-testid="hero-brand-block"
            >
              <img
                src="/brand/dc-logo.png"
                alt="DuoClair — simbolo DC metallico"
                className="h-auto w-[190px] sm:w-[235px]"
                draggable="false"
              />
              <span className="wordmark-3d -mt-3 font-display text-[13vw] leading-none tracking-tight sm:-mt-4 sm:text-6xl">
                DuoClair
              </span>
              <span className="tagline-elegant mt-3 text-[9px] text-zinc-300 sm:text-[11px]">
                Marketing Locale
              </span>
            </div>

            <div className="mt-2 headline-3d">
              <MaskedLines
                as="h1"
                delay={0.35}
                lines={[
                  <span className="chrome-text">La tua attività.</span>,
                  <span className="gold-text">Più forte online.</span>,
                ]}
                className="font-display leading-[1.15] tracking-tight text-[7.8vw] sm:text-4xl"
                data-testid="hero-headline"
              />
            </div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-3 text-[13px] sm:text-sm text-zinc-300 leading-relaxed max-w-sm mx-auto"
              data-testid="hero-subline"
            >
              Recensioni, social, grafiche e video per migliorare la presenza digitale della tua attività.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              onClick={() => {
                scrollTo("#video");
                window.dispatchEvent(new CustomEvent("duoclair:play-video"));
              }}
              data-testid="hero-video-cta"
              className="mt-6 group inline-flex items-center gap-3 rounded-full border border-gold/45 bg-black/40 pl-2.5 pr-6 py-2.5 hover:border-gold/75 hover:bg-gold/5 transition-all duration-300"
            >
              <span className="w-8 h-8 rounded-full border border-gold/60 bg-black/50 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                <Play className="w-3 h-3 text-gold-light fill-gold-light ml-0.5" />
              </span>
              <span className="font-sans font-medium text-[11px] sm:text-xs uppercase tracking-[0.26em] text-slate-200">
                Guarda il video
              </span>
            </motion.button>
          </div>
        </section>

        {/* Video */}
        <section id="video" className="relative px-5 pb-12 sm:pb-16 scroll-mt-16" data-testid="video-section">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeUp} className="flex items-center justify-center gap-4 mb-5">
              <span className="gold-hairline w-12 sm:w-20" />
              <p className="font-sans font-light text-[10px] sm:text-xs uppercase tracking-[0.35em] text-zinc-300 whitespace-nowrap">
                La presentazione · 60 secondi
              </p>
              <span className="gold-hairline w-12 sm:w-20" />
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="rounded-2xl border border-gold/25 bg-black overflow-hidden shadow-[0_30px_90px_-35px_rgba(0,0,0,0.9)]"
            >
              <VideoPlayer src={videoUrl} />
            </motion.div>
          </div>
        </section>

        {/* Services CTA — replaces the old 4-card grid */}
        <section id="soluzioni" className="px-5 py-10 sm:py-14 scroll-mt-16" data-testid="services-section">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              {...fadeUp}
              className="font-display headline-3d leading-[1.15] tracking-tight text-xl sm:text-3xl mb-4"
              data-testid="services-heading"
            >
              <span className="chrome-text">Cosa possiamo fare per </span>
              <span className="gold-text">la tua attività?</span>
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 }}
              className="text-[13px] sm:text-sm text-zinc-300 leading-relaxed max-w-md mx-auto mb-7"
            >
              Dalla presenza sui social ai siti, dai contenuti alle automazioni: scopri le soluzioni DuoClair pensate per rendere la tua attività più forte online.
            </motion.p>
            <motion.button
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              onClick={goToServizi}
              data-testid="services-cta-button"
              className="group inline-flex items-center gap-3 rounded-full border border-gold/45 bg-black/40 px-8 py-3.5 hover:border-gold/75 hover:bg-gold/5 transition-all duration-300"
            >
              <span className="font-sans font-medium text-[11px] sm:text-xs uppercase tracking-[0.26em] text-slate-200">
                Scopri i nostri servizi
              </span>
              <ArrowRight className="w-4 h-4 text-gold-light transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </div>
        </section>

        {/* Conversion */}
        <section className="px-5 pb-12 sm:pb-16" data-testid="conversion-section">
          <motion.div
            {...fadeUp}
            className="relative max-w-3xl mx-auto bg-[#0D0D10]/75 border border-white/10 rounded-2xl px-5 py-8 sm:px-10 sm:py-10 text-center overflow-hidden"
          >
            <div className="absolute inset-x-10 top-0 gold-hairline" />
            <h2
              className="font-display headline-3d leading-[1.2] tracking-tight text-lg sm:text-2xl"
              data-testid="conversion-heading"
            >
              <span className="chrome-text block">Scopri cosa possiamo</span>
              <span className="gold-text block">migliorare nella tua attività.</span>
            </h2>
            <button
              onClick={() => setFormOpen(true)}
              data-testid="conversion-cta-button"
              className="btn-champagne mt-6 rounded-full px-8 sm:px-10 py-3.5 font-bold text-sm sm:text-base tracking-tight hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300"
            >
              Richiedi un'analisi gratuita
            </button>
            <p className="mt-4 font-sans font-light text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-zinc-400">
              Gratuita · 60 secondi · Senza impegno
            </p>
            <p className="mt-2 font-sans font-light text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-gold/60">
              Sblocca l'accesso al listino riservato
            </p>
          </motion.div>
        </section>

        {/* Footer — links only, no repeated brand block */}
        <footer id="contatti" className="border-t border-white/5 py-6 px-5 scroll-mt-16">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-4 gap-y-2.5 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.18em]">
            <a
              href={DUOCLAIR_WHATSAPP.link}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-link"
              className="inline-flex items-center gap-2 text-zinc-300 hover:text-gold-light transition-colors"
            >
              WhatsApp <span className="text-zinc-400">{DUOCLAIR_WHATSAPP.display}</span>
            </a>
            <span className="text-zinc-700">|</span>
            <a
              href="https://instagram.com/duoclair"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram-link"
              className="text-zinc-300 hover:text-gold-light transition-colors"
            >
              @duoclair
            </a>
            <span className="text-zinc-700">|</span>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-zinc-300 hover:text-gold-light transition-colors"
            >
              Privacy
            </Link>
            <span className="text-zinc-700">|</span>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-zinc-500 hover:text-gold-light transition-colors"
            >
              Accesso Partner
            </Link>
          </div>
        </footer>
      </div>

      {/* Analysis form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent
          data-testid="analysis-dialog"
          className="bg-[#0B0B0D] border-gold/25 text-slate-100 w-[calc(100vw-1.5rem)] sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-2xl p-5 sm:p-8"
        >
          <DialogHeader>
            <DialogTitle className="font-sans font-extrabold text-xl sm:text-2xl text-slate-100">
              Richiedi un'analisi <span className="gold-text">gratuita</span>
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Otto domande, meno di un minuto. Ti ricontattiamo entro 24 ore.
            </DialogDescription>
          </DialogHeader>
          <LeadForm />
        </DialogContent>
      </Dialog>
    </div>
  );
}
